<!-- Responsibility: Implement atlas map behavior for the ui/components surface.
Scope: Owns atlas map rendering + interaction only; cross-surface orchestration is excluded. -->
<atlas-map>
  <div
    class="atlas-map"
    ref="container"
    data-variant={ variant() }
    data-expanded={ expanded() ? 'true' : 'false' }
    data-dragging={ state.dragging ? 'true' : 'false' }
    onwheel={ onWheelZoom }
    onpointerdown={ beginDrag }
    onpointermove={ dragMap }
    onpointerup={ endDrag }
    onpointerleave={ endDrag }
  >
    <svg
      if={ state.projection }
      aria-label="Atlas map"
      viewBox={ `0 0 ${state.width} ${state.height}` }
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <clipPath each={ node in thumbnailNodes() } id={ thumbnailClipId(node) }>
          <circle
            if={ node.kind !== 'passage' }
            cx="0"
            cy="0"
            r={ nodeRadius(node) - 1.25 }
          ></circle>
          <path if={ node.kind === 'passage' } d={ thumbnailClipPath(node) }></path>
        </clipPath>
        <linearGradient
          each={ gradient in paletteGradients() }
          id={ gradient.id }
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stop-color={ gradient.start }></stop>
          <stop offset="100%" stop-color={ gradient.end }></stop>
        </linearGradient>
      </defs>

      <g transform={ stageTransform() }>
        <path
          each={ edge in state.projection.edges }
          class="atlas-link-underlay"
          d={ edgePath(edge) }
          data-frontier={ edge.source.isFrontier || edge.target.isFrontier ? 'true' : 'false' }
          data-source-kind={ edge.source.kind }
          data-target-kind={ edge.target.kind }
        ></path>
        <path
          each={ edge in state.projection.edges }
          class="atlas-link"
          d={ edgePath(edge) }
          data-frontier={ edge.source.isFrontier || edge.target.isFrontier ? 'true' : 'false' }
          data-source-kind={ edge.source.kind }
          data-target-kind={ edge.target.kind }
          style={ edgeStrokeStyle(edge) }
        ></path>

        <g
          each={ node in renderNodes() }
          class="atlas-node-group"
          data-node-id={ node.id }
          data-kind={ node.kind }
          data-current={ node.isCurrent ? 'true' : 'false' }
          data-frontier={ node.isFrontier ? 'true' : 'false' }
          data-highlighted={ state.tooltip?.id === node.id ? 'true' : 'false' }
          transform={ `translate(${node.screenX},${node.screenY})` }
          onclick={ (event) => onNodeClick(event, node) }
          onkeydown={ (event) => onNodeKeydown(event, node) }
          onmouseenter={ (event) => showNodeTooltip(event, node) }
          onmouseleave={ hideNodeTooltip }
          onfocus={ (event) => showNodeTooltip(event, node) }
          onblur={ hideNodeTooltip }
          tabindex="0"
          role="button"
          aria-label={ `${node.name} · ${node.summary}` }
          data-palette-source={ paletteSource(node) ? 'image' : 'fallback' }
        >
          <rect
            if={ node.kind === 'passage' }
            x={ nodeRect(node).x - 6 }
            y={ nodeRect(node).y - 6 }
            width={ nodeRect(node).width + 12 }
            height={ nodeRect(node).height + 12 }
            fill="transparent"
            class="atlas-hitbox"
          ></rect>
          <circle
            if={ node.kind !== 'passage' }
            r={ nodeRadius(node) + 22 }
            fill="transparent"
            class="atlas-hitbox"
          ></circle>
          <image
            if={ nodeMediaUrl(node) }
            class="atlas-node-thumb"
            href={ nodeMediaUrl(node) }
            x={ thumbnailBounds(node).x }
            y={ thumbnailBounds(node).y }
            width={ thumbnailBounds(node).width }
            height={ thumbnailBounds(node).height }
            preserveAspectRatio="xMidYMid slice"
            clip-path={ `url(#${thumbnailClipId(node)})` }
          ></image>
          <rect
            if={ node.kind === 'passage' }
            class="atlas-node atlas-route-node"
            x={ nodeRect(node).x }
            y={ nodeRect(node).y }
            width={ nodeRect(node).width }
            height={ nodeRect(node).height }
            rx={ nodeRect(node).radius }
            ry={ nodeRect(node).radius }
            style={ nodeStyle(node) }
          ></rect>
          <circle
            if={ node.kind !== 'passage' }
            class="atlas-node atlas-room-node"
            r={ nodeRadius(node) }
            style={ nodeStyle(node) }
          ></circle>
          <text
            if={ !nodeMediaUrl(node) }
            class="atlas-node-icon"
            dy="0.35em"
          >{ nodeIcon(node) }</text>
          <circle
            if={ node.isCurrent }
            class="atlas-pulse-ring"
            r={ nodeRadius(node) + 7 }
            style={ nodePulseStyle(node) }
          ></circle>
          <text
            if={ node.isCurrent }
            class="atlas-you"
            dy={ node.kind === 'passage' ? '-2.85em' : '-2.35em' }
          >YOU</text>
          <text class="atlas-label" dy={ node.kind === 'passage' ? '-1.3em' : '1.85em' }>{ node.shortLabel }</text>
          <text if={ expanded() && node.summary } class="atlas-meta" dy={ node.kind === 'passage' ? '2.4em' : '3.05em' }>{ node.summary }</text>
        </g>
      </g>
    </svg>

    <header if={ variant() !== 'mini' }>
      <div>
        <strong>ATLAS</strong>
        <span>{ state.projection ? `${state.projection.nodes.length} nodes · ${state.model?.frontierCount || 0} frontiers` : 'Waiting for atlas' }</span>
      </div>
      <nav aria-label="Atlas controls">
        <button type="button" onclick={ zoomOut } aria-label="Zoom out atlas">−</button>
        <button type="button" onclick={ recenterMap } aria-label="Recenter atlas">⌖</button>
        <button type="button" onclick={ zoomIn } aria-label="Zoom in atlas">+</button>
      </nav>
    </header>

    <div class="atlas-legend" aria-label="Atlas status color legend">
      <span><i data-kind="current"></i>Current room</span>
      <span><i data-kind="room"></i>Known room</span>
      <span><i data-kind="passage"></i>Passage</span>
      <span><i data-kind="path"></i>Known path</span>
    </div>

    <aside
      if={ state.tooltip }
      class="atlas-node-tooltip"
      data-side={ state.tooltip.sideTop ? 'top' : 'bottom' }
      style={ tooltipStyle() }
      aria-live="polite"
    >
      <div class="media">
        <img if={ state.tooltip.imageUrl } src={ state.tooltip.imageUrl } alt="">
        <span if={ !state.tooltip.imageUrl }>{ state.tooltip.icon }</span>
      </div>
      <div class="copy">
        <strong>{ state.tooltip.name }</strong>
        <span>{ state.tooltip.summary }</span>
      </div>
    </aside>
  </div>

  <script>
    import * as gameStore from '../../game-store.js'
    import { dispatcher } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { openItemDialog, openNpcDialog } from '../dialog/dialog-openers.ts'
    import { buildAtlasViewModel, projectAtlasViewModel } from '../atlas/atlas-view-model.ts'
    import { atlasPaletteFromCssGradient, deriveAtlasNodePalette, fallbackAtlasNodePalette } from '../atlas/atlas-node-palette.ts'
    import {
      nodeRadius,
      nodeRect,
      edgePath,
      thumbnailClipPath,
      thumbnailBounds,
    } from '../atlas/atlas-projection.ts'

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value))
    let atlasMapInstanceCounter = 0

    export default {
      state: {
        width: 0,
        height: 0,
        zoom: 1,
        panX: 0,
        panY: 0,
        dragging: false,
        projection: null,
        model: null,
        nodePalettes: {},
        tooltip: null,
      },

      onMounted() {
        this.gradientPrefix = `atlas-node-fill-${++atlasMapInstanceCounter}`
        this.syncProjection(true)
        this.roomChangeHandle = dispatcher.on(TOPICS.game.roomChanged, () => this.onRoomChanged())
        this.resizeHandle = () => this.syncProjection()
        window.addEventListener('resize', this.resizeHandle)
      },

      onUnmounted() {
        if (this.roomChangeHandle) dispatcher.off(this.roomChangeHandle)
        if (this.resizeHandle) window.removeEventListener('resize', this.resizeHandle)
        if (this.tooltipHideTimer) clearTimeout(this.tooltipHideTimer)
      },

      onUpdated() {
        this.syncProjection()
        this.syncNodePalettes()
      },

      atlasData() {
        return this.props.view?.atlas ?? this.props.atlas
      },

      locationData() {
        return this.props.view?.location ?? this.props.location
      },

      currentRoomData() {
        return this.props.view?.currentRoom ?? this.props.currentRoom
      },

      currentVisionUrl() {
        return this.props.view?.currentVisionUrl ?? this.props.currentVisionUrl
      },

      variant() {
        return this.props.view?.variant ?? this.props.variant ?? 'panel'
      },

      expanded() {
        return this.props.view?.expanded ?? this.props.expanded ?? false
      },

      onRoomChanged() {
        this.recenterMap()
        this.hideNodeTooltip()
        dispatcher.trigger('atlas-map', TOPICS.ui.dialogClose)
      },

      async openAtlasNpc(npc) {
        await openNpcDialog('atlas-map', npc)
      },

      async openAtlasItem(itemName) {
        await openItemDialog('atlas-map', itemName, { location: 'room' })
      },

      syncProjection(forceRecenter = false) {
        const container = this.$('div.atlas-map')
        const atlas = this.atlasData()
        const location = this.locationData()
        if (!container || !atlas || !location) return

        const width = Math.max(220, Math.round(container.clientWidth || 0))
        const height = Math.max(
          this.variant() === 'mini' ? 120 : (this.expanded() ? 420 : 240),
          Math.round(container.clientHeight || 0),
        )
        const model = buildAtlasViewModel(atlas, location)
        const projection = projectAtlasViewModel(model, { width, height }, this.expanded() ? 110 : 86)
        const signature = `${model.currentNodeId}:${projection.nodes.length}:${width}:${height}:${this.expanded() ? '1' : '0'}:${this.variant()}`
        if (!forceRecenter && this.projectionSignature === signature) return

        this.projectionSignature = signature
        this.update({
          width,
          height,
          model,
          projection,
          ...(forceRecenter || !this.state.projection ? { zoom: 1, panX: 0, panY: 0 } : {}),
        })
        this.syncNodePalettes()
      },

      stageTransform() {
        const zoom = Number(this.state.zoom || 1)
        const panX = Number(this.state.panX || 0)
        const panY = Number(this.state.panY || 0)
        const width = Number(this.state.width || 0)
        const height = Number(this.state.height || 0)
        const cx = width / 2
        const cy = height / 2
        return `translate(${cx + panX},${cy + panY}) scale(${zoom}) translate(${-cx},${-cy})`
      },

      thumbnailNodes() {
        return this.state.projection?.nodes ?? []
      },

      renderNodes() {
        const nodes = [...(this.state.projection?.nodes ?? [])]
        const rank = (node) => {
          if (node.isCurrent) return 2
          if (node.kind === 'passage') return 0
          return 1
        }
        return nodes.sort((a, b) => {
          const rankDelta = rank(a) - rank(b)
          if (rankDelta !== 0) return rankDelta
          const yDelta = Number(a.screenY || 0) - Number(b.screenY || 0)
          if (yDelta !== 0) return yDelta
          const xDelta = Number(a.screenX || 0) - Number(b.screenX || 0)
          if (xDelta !== 0) return xDelta
          return String(a.id).localeCompare(String(b.id))
        })
      },

      thumbnailClipId(node) {
        const prefix = this.gradientPrefix || 'atlas-node-thumb'
        return `${prefix}-clip-${String(node.id).replace(/[^a-zA-Z0-9_-]/g, '-')}`
      },

      paletteSource(node) {
        if (node.isCurrent) return String(this.currentVisionUrl() || gameStore.state?.visionUrl || node.visionUrl || '').trim()
        return String(node.visionUrl || '').trim()
      },

      nodeMediaUrl(node) {
        return this.paletteSource(node)
      },

      paletteFor(node) {
        return this.state.nodePalettes?.[node.id] || fallbackAtlasNodePalette(node.kind, node.isCurrent)
      },

      paletteGradientId(node) {
        const prefix = this.gradientPrefix || 'atlas-node-fill'
        return `${prefix}-${String(node.id).replace(/[^a-zA-Z0-9_-]/g, '-')}`
      },

      paletteGradients() {
        const nodes = this.state.projection?.nodes ?? []
        return nodes.map((node) => {
          const palette = this.paletteFor(node)
          return {
            id: this.paletteGradientId(node),
            start: palette.start,
            end: palette.end,
          }
        })
      },

      nodeStyle(node) {
        const palette = this.paletteFor(node)
        if (this.nodeMediaUrl(node)) {
          return `fill:rgba(6,12,20,0.18);stroke:${palette.stroke};--atlas-node-glow:${palette.glow};`
        }
        return `fill:url(#${this.paletteGradientId(node)});stroke:${palette.stroke};--atlas-node-glow:${palette.glow};`
      },

      nodePulseStyle(node) {
        const palette = this.paletteFor(node)
        return `stroke:${palette.stroke};`
      },

      edgeStrokeStyle(edge) {
        const source = this.paletteFor(edge.source)
        const target = this.paletteFor(edge.target)
        return `--atlas-edge-start:${source.stroke};--atlas-edge-end:${target.stroke};`
      },

      nodeIcon(node) {
        const name = String(node.name || '').toLowerCase()
        if (node.kind === 'passage') return node.axis === 'vertical' ? '↕' : '↔'
        if (/archive|ledger|library|record/.test(name)) return '📚'
        if (/observatory|spire|tower|watch/.test(name)) return '🔭'
        if (/forge|smith|anvil/.test(name)) return '⚒️'
        if (/garden|grove|forest|wild|bloom/.test(name)) return '🌿'
        if (/lab|bio|reactor|alchem|chrysalis/.test(name)) return '🧪'
        if (/market|bazaar|stall|trade/.test(name)) return '🛍️'
        if (/sanctum|temple|shrine|chapel/.test(name)) return '🕯️'
        if (/gallery|hall|atrium|chamber|room/.test(name)) return '🏛️'
        return node.isCurrent ? '✨' : '◈'
      },

      tooltipStyle() {
        const tip = this.state.tooltip
        if (!tip) return ''
        return `left:${tip.left}px;top:${tip.top}px;--atlas-tip-shift:${tip.shift}px;`
      },

      showNodeTooltip(event, node) {
        if (this.tooltipHideTimer) {
          clearTimeout(this.tooltipHideTimer)
          this.tooltipHideTimer = null
        }
        const container = this.$('div.atlas-map')
        const target = event?.currentTarget
        if (!(container instanceof HTMLElement) || !(target instanceof SVGElement || target instanceof HTMLElement)) return
        const containerRect = container.getBoundingClientRect()
        const targetRect = target.getBoundingClientRect()
        const left = Math.max(12, Math.min((targetRect.left - containerRect.left) + (targetRect.width / 2), containerRect.width - 12))
        const sideTop = (targetRect.top - containerRect.top) > 118
        const top = sideTop
          ? Math.max(10, (targetRect.top - containerRect.top) - 14)
          : Math.min(containerRect.height - 12, (targetRect.bottom - containerRect.top) + 14)
        const shift = Math.max(-96, Math.min(96, (containerRect.width / 2) - left))
        const current = this.state.tooltip
        if (
          current
          && current.id === node.id
          && current.sideTop === sideTop
          && Math.abs((current.left || 0) - left) < 2
          && Math.abs((current.top || 0) - top) < 2
        ) return
        this.update({
          tooltip: {
            id: node.id,
            name: node.name,
            summary: node.summary,
            imageUrl: this.nodeMediaUrl(node) || '',
            icon: this.nodeIcon(node),
            left,
            top,
            shift,
            sideTop,
          },
        })
      },

      hideNodeTooltip() {
        if (this.tooltipHideTimer) clearTimeout(this.tooltipHideTimer)
        this.tooltipHideTimer = setTimeout(() => {
          this.tooltipHideTimer = null
          if (this.state.tooltip) this.update({ tooltip: null })
        }, 90)
      },

      async syncNodePalettes() {
        const nodes = this.state.projection?.nodes ?? []
        if (!nodes.length) return
        const next = { ...(this.state.nodePalettes ?? {}) }
        let changed = false
        await Promise.all(nodes.map(async (node) => {
          const source = this.paletteSource(node)
          if (!source) {
            const fallback = fallbackAtlasNodePalette(node.kind, node.isCurrent)
            const current = next[node.id]
            if (!current || JSON.stringify(current) !== JSON.stringify(fallback)) {
              next[node.id] = fallback
              changed = true
            }
            return
          }
          const derived = await deriveAtlasNodePalette(source)
          const palette = derived || atlasPaletteFromCssGradient(source, node.kind, node.isCurrent) || fallbackAtlasNodePalette(node.kind, node.isCurrent)
          const current = next[node.id]
          if (!current || JSON.stringify(current) !== JSON.stringify(palette)) {
            next[node.id] = palette
            changed = true
          }
        }))
        if (changed) this.update({ nodePalettes: next })
      },

      zoomIn() {
        this.update({ zoom: clamp(Number(this.state.zoom || 1) + 0.18, 0.72, 2.6) })
      },

      zoomOut() {
        this.update({ zoom: clamp(Number(this.state.zoom || 1) - 0.18, 0.72, 2.6) })
      },

      recenterMap() {
        this.update({ zoom: 1, panX: 0, panY: 0, dragging: false })
      },

      onWheelZoom(event) {
        if (this.variant() === 'mini') return
        event.preventDefault()
        const invert = this.props.view?.invertZoom ?? this.props.invertZoom ?? false
        const delta = event.deltaY > 0 ? (invert ? 0.12 : -0.12) : (invert ? -0.12 : 0.12)
        this.update({ zoom: clamp(Number(this.state.zoom || 1) + delta, 0.72, 2.6) })
      },

      beginDrag(event) {
        if (this.variant() === 'mini' || event.target?.closest?.('button')) return
        this.dragOrigin = {
          x: event.clientX,
          y: event.clientY,
          panX: Number(this.state.panX || 0),
          panY: Number(this.state.panY || 0),
        }
        this.update({ dragging: true })
      },

      dragMap(event) {
        if (!this.state.dragging || !this.dragOrigin) return
        this.update({
          panX: this.dragOrigin.panX + (event.clientX - this.dragOrigin.x),
          panY: this.dragOrigin.panY + (event.clientY - this.dragOrigin.y),
        })
      },

      endDrag() {
        this.dragOrigin = null
        if (this.state.dragging) this.update({ dragging: false })
      },

      onNodeKeydown(event, node) {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        this.onNodeClick(event, node)
      },

      isCurrentNode(node) {
        const location = this.locationData()
        return !!location && node.id === `${location.x}_${location.y}_${location.z ?? 0}`
      },

      onNodeClick(event, node) {
        event.stopPropagation()
        const gs = gameStore.state
        if (!gs) return

        const isCur = this.isCurrentNode(node)
        const remoteFlags = node.flags ?? {}
        const hasRemoteAccess = !!(
          remoteFlags.remote_access === true ||
          remoteFlags.remoteAccess === true ||
          remoteFlags.allow_remote_interaction === true ||
          remoteFlags.allowRemoteInteraction === true
        )
        const canInteractRemotely = isCur || hasRemoteAccess
        const currentRoom = this.currentRoomData()
        const roomItems = isCur ? (currentRoom?.items ?? []) : (node.items ?? [])
        const roomNpcs = isCur
          ? (currentRoom?.npcs ?? [])
          : (gs.world?.npcs ?? [])
              .filter(n => n.location === node.id)
              .map(n => ({
                name: n.name,
                identity: n.identity,
                lore: n.lore,
                relationship: n.relationship ?? 0,
                memory: n.memory ?? [],
                intentions: n.intentions ?? [],
                feelings: n.feelings ?? {},
                activeEffects: n.activeEffects ?? [],
                dormantEffects: n.dormantEffects ?? [],
              }))
        const activeConditions = Object.entries(node.flags ?? {})
          .filter(([_, value]) => value !== false && value !== null)
          .map(([key, value]) => value === true ? key.replace(/_/g, ' ').toUpperCase() : `${key.replace(/_/g, ' ').toUpperCase()}: ${value}`)
        const dormantConditions = isCur
          ? (currentRoom?.dormantFlags ?? node.dormantFlags ?? [])
          : (node.dormantFlags ?? [])
        const conditionBlock = [
          '**ACTIVE CONDITIONS**',
          activeConditions.length ? activeConditions.map(c => `• ${c}`).join('\n') : 'None',
          '',
          '**DORMANT CONDITIONS**',
          dormantConditions.length
            ? dormantConditions.map(c => typeof c === 'string' ? `• ${c}` : `• ${c.label || c.key || 'Condition'}`).join('\n')
            : 'None',
        ].join('\n')
        const quests = (gs.journal ?? []).filter(q => q.status === 'active').slice(0, 5)
        const hasTravelAction = !isCur && node.distanceFromCurrent === 1 && ['north', 'east', 'south', 'west', 'up', 'down'].includes(node.directionFromCurrent)
        const actionLabel = node.kind === 'passage'
          ? `TAKE ${String(node.directionFromCurrent).toUpperCase()} PASSAGE`
          : `GO ${String(node.directionFromCurrent).toUpperCase()}`

        const locationDesc = isCur ? (currentRoom?.desc || node.desc || 'A location you have visited.') : (node.desc || 'A location you have visited.')
        const remoteNote = !canInteractRemotely ? '\n\n**REMOTE VIEW**\nInteraction is disabled for this location.' : ''

        dispatcher.trigger('atlas-map', TOPICS.ui.dialogOpen, {
          content: 'location-dialog',
          data: {
            locationName: node.name,
            items: roomItems,
            npcs: roomNpcs,
            quests,
            conditions: conditionBlock,
            allowEntityInteraction: canInteractRemotely,
            onOpenNpc: (npc) => this.openAtlasNpc(npc),
            onOpenItem: (item) => this.openAtlasItem(item),
            media: {
              imageKind: 'vision',
              imageSeed: (isCur ? currentRoom?.desc : node.desc) || node.name,
              imageUrl: isCur ? (gs.visionUrl || node.visionUrl) : node.visionUrl,
              fallbackLabel: node.name,
            },
          },
          options: {
            title: node.name,
            desc: locationDesc + remoteNote,
            meta: (node.kind === 'passage' ? 'PASSAGE' : 'ROOM') + ' \u00b7 ' + node.summary + ' \u00b7 What is/was there: ' + roomItems.length + ' \u00b7 Who is/was there: ' + roomNpcs.length,
            actions: hasTravelAction
              ? [{ label: actionLabel, input: 'Go ' + node.directionFromCurrent }]
              : [],
          },
        })
      },

      nodeRadius,
      nodeRect,
      edgePath,
      thumbnailClipPath,
      thumbnailBounds,
    }
  </script>

  <style>
    atlas-map,
    .atlas-map {
      display: block;
      width: 100%;
      min-width: 0;
    }

    .atlas-map {
      --atlas-height: 15.75rem;
      position: relative;
      min-height: var(--atlas-height);
      height: var(--atlas-height);
      overflow: hidden;
      border: 1px solid color-mix(in srgb, var(--border) var(--alpha-ghost), transparent);
      border-radius: var(--space-md);
      background:
        radial-gradient(circle at var(--alpha-low) var(--alpha-low), color-mix(in srgb, var(--gold) var(--alpha-glow), transparent), transparent var(--alpha-subtle)),
        radial-gradient(circle at var(--alpha-high) var(--alpha-low), color-mix(in srgb, var(--brand-primary, var(--brand-primary)) var(--alpha-low), transparent), transparent var(--alpha-low)),
        linear-gradient(180deg, color-mix(in srgb, #08111d var(--alpha-subtle), transparent), color-mix(in srgb, #03060c var(--alpha-subtle), transparent));
      box-shadow:
        0 1.375rem 2.625rem rgba(0, 0, 0, 0.28),
        inset 0 1px 0 rgba(255, 255, 255, 0.06);
      isolation: isolate;
      animation: atlas-map-enter 0.62s ease-out both;
    }

    .atlas-map[data-expanded='true'] {
      --atlas-height: min(58vh, 34rem);
    }

    .atlas-map[data-variant='mini'] {
      --atlas-height: 100%;
      min-height: 100%;
      height: 100%;
      border-radius: inherit;
      border: none;
      box-shadow: none;
      background: transparent;
      animation: none;
    }

    .atlas-map::before {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      background:
        linear-gradient(135deg, color-mix(in srgb, #f8fbff var(--alpha-glow), transparent), transparent var(--alpha-mid)),
        radial-gradient(circle at var(--alpha-focus) var(--alpha-subtle), rgba(255, 255, 255, 0.04), transparent var(--alpha-low));
      opacity: 0.8;
      animation: atlas-sheen 1.1s ease-out both;
    }

    .atlas-map > header {
      position: absolute;
      inset: var(--space-sm) var(--space-sm) auto;
      z-index: 4;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--space-sm);
      pointer-events: none;
    }

    .atlas-map > header > div,
    .atlas-map > header > nav {
      pointer-events: auto;
    }

    .atlas-map > header > div {
      display: grid;
      gap: var(--space-xs);
    }

    .atlas-map > header strong,
    .atlas-map > header span {
      margin: 0;
      display: block;
    }

    .atlas-map > header strong {
      color: color-mix(in srgb, var(--gold) var(--alpha-high), var(--text-prose));
      font-size: 0.68rem;
      letter-spacing: 0.18em;
    }

    .atlas-map > header span {
      color: var(--text-dim, #93a8c2);
      font-size: var(--space-sm);
    }

    .atlas-map > header > nav {
      display: inline-flex;
      gap: var(--space-xs);
    }

    .atlas-map > header > nav > button {
      min-width: 2.3rem;
      min-height: 2.3rem;
      border-radius: 62.4375rem;
      border: 1px solid color-mix(in srgb, var(--border) var(--alpha-high), transparent);
      background: color-mix(in srgb, rgba(5, 10, 18, 0.9) var(--alpha-high), transparent);
      color: var(--text-prose, var(--text-prose));
      font: inherit;
      cursor: pointer;
      transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;
    }

    .atlas-map > header > nav > button:hover,
    .atlas-map > header > nav > button:focus-visible {
      outline: none;
      transform: translateY(-1px);
      border-color: color-mix(in srgb, var(--gold) var(--alpha-mid), transparent);
      background: color-mix(in srgb, rgba(10, 18, 30, 0.96) var(--alpha-high), transparent);
    }

    .atlas-map > svg {
      width: 100%;
      height: 100%;
      position: relative;
      z-index: 1;
      touch-action: none;
    }

    .atlas-link,
    .atlas-link-underlay {
      fill: none;
      stroke-width: 3.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .atlas-link {
      stroke: linear-gradient(90deg, var(--atlas-edge-start), var(--atlas-edge-end));
      stroke: color-mix(in srgb, var(--atlas-edge-start, rgba(213, 228, 255, 0.82)) 50%, var(--atlas-edge-end, rgba(120, 156, 214, 0.82)));
      opacity: 0.8;
      filter: drop-shadow(0 0 0.625rem color-mix(in srgb, var(--atlas-edge-end, rgba(139, 180, 255, 0.22)) var(--alpha-mid), transparent));
      animation: atlas-route-flow 4.6s linear infinite;
    }

    .atlas-link-underlay {
      stroke: rgba(5, 10, 19, 0.92);
      stroke-width: 7.8;
      opacity: 0.96;
      filter: none;
      animation: none;
    }

    .atlas-link[data-frontier='true'] {
      stroke-dasharray: 6 6;
      opacity: 0.94;
      animation-duration: 2.6s;
    }

    .atlas-node-group {
      cursor: pointer;
      transition: filter 0.18s ease, opacity 0.18s ease;
    }

    .atlas-node-group[data-highlighted='true'],
    .atlas-node-group:focus-visible {
      filter: brightness(1.06);
    }

    .atlas-node {
      stroke-width: 1.8;
      filter:
        drop-shadow(0 0.625rem 1.375rem rgba(0, 0, 0, 0.34))
        drop-shadow(0 0 0.75rem var(--atlas-node-glow, rgba(112, 161, 238, 0.24)));
    }

    .atlas-node-group[data-kind='passage'] .atlas-node {
      animation: atlas-route-breathe 3.8s ease-in-out infinite;
    }

    .atlas-node-group[data-current='true'] .atlas-node {
      stroke-width: 2.5;
      animation: atlas-focus-enter 0.48s cubic-bezier(0.22, 0.9, 0.22, 1.15) both;
    }

    .atlas-node-thumb {
      pointer-events: none;
      opacity: 0.92;
      filter:
        saturate(1.08)
        contrast(1.04)
        brightness(1.02);
    }

    .atlas-node-icon {
      fill: #eef5ff;
      text-anchor: middle;
      font-size: 0.8125rem;
      pointer-events: none;
      filter: drop-shadow(0 0.125rem 0.5rem rgba(0, 0, 0, 0.38));
    }

    .atlas-node-group[data-frontier='true'] .atlas-node {
      stroke-dasharray: 5 4;
    }

    .atlas-node-group[data-highlighted='true'] .atlas-node,
    .atlas-node-group:focus-visible .atlas-node {
      stroke-width: 2.3;
    }

    .atlas-pulse-ring {
      fill: none;
      stroke-width: 2;
      animation: atlas-pulse-ring 2.8s ease-in-out infinite;
    }

    .atlas-you {
      fill: color-mix(in srgb, var(--gold) var(--alpha-high), var(--text-prose));
      text-anchor: middle;
      pointer-events: none;
      font-size: 0.5625rem;
      font-weight: 800;
      letter-spacing: 0.18em;
      filter: drop-shadow(0 0.125rem 0.5rem rgba(0, 0, 0, 0.52));
    }

    .atlas-label,
    .atlas-meta {
      fill: #e7eefb;
      text-anchor: middle;
      pointer-events: none;
    }

    .atlas-label {
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.04em;
    }

    .atlas-node-group[data-kind='passage'] .atlas-label {
      fill: #d6e5ff;
    }

    .atlas-meta {
      font-size: 0.5625rem;
      fill: rgba(188, 205, 229, 0.84);
      opacity: 0.92;
    }

    .atlas-legend {
      position: absolute;
      left: var(--space-sm);
      bottom: var(--space-sm);
      z-index: 4;
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-xs);
      max-width: calc(100% - 1.4rem);
      pointer-events: none;
    }

    .atlas-legend > span {
      display: inline-flex;
      align-items: center;
      gap: var(--space-xs);
      min-height: var(--space-lg);
      padding: 0.16rem var(--space-xs);
      border-radius: 62.4375rem;
      color: #d8e4f7;
      border: 1px solid rgba(94, 116, 150, 0.72);
      background: rgba(4, 9, 18, 0.84);
      font-size: var(--type-3xs);
      letter-spacing: 0.05em;
    }

    .atlas-legend i {
      display: inline-block;
      flex-shrink: 0;
    }

    .atlas-legend i[data-kind='current'],
    .atlas-legend i[data-kind='room'] {
      width: 0.6rem;
      height: 0.6rem;
      border-radius: 62.4375rem;
      border: 1px solid rgba(223, 235, 255, 0.6);
      background: rgba(24, 37, 59, 0.9);
    }

    .atlas-legend i[data-kind='current'] {
      background: rgba(246, 194, 62, 0.98);
      border-color: rgba(252, 219, 122, 0.96);
    }

    .atlas-legend i[data-kind='passage'] {
      width: var(--space-md);
      height: var(--space-xs);
      border-radius: 62.4375rem;
      border: 1px solid rgba(140, 176, 229, 0.76);
      background: rgba(11, 18, 31, 0.96);
    }

    .atlas-legend i[data-kind='path'] {
      width: var(--space-md);
      border-top: 0.125rem solid rgba(206, 225, 255, 0.84);
    }

    .atlas-node-tooltip {
      position: absolute;
      z-index: var(--z-tooltip, 9000);
      min-width: 11rem;
      max-width: 15rem;
      display: grid;
      grid-template-columns: 3rem minmax(0, 1fr);
      gap: 0.65rem;
      align-items: center;
      padding: 0.62rem var(--space-sm);
      border-radius: var(--space-md);
      border: 1px solid color-mix(in srgb, var(--border) var(--alpha-ghost), rgba(255,255,255,0.16));
      background:
        linear-gradient(180deg, rgba(5, 10, 18, 0.96), rgba(3, 6, 12, 0.98)),
        radial-gradient(circle at var(--alpha-low) var(--alpha-low), rgba(255,255,255,0.08), transparent var(--alpha-mid));
      box-shadow:
        0 1.5rem 3.125rem rgba(0, 0, 0, 0.42),
        0 0 0 1px rgba(255, 255, 255, 0.04) inset;
      transform: translate(calc(-50% + var(--atlas-tip-shift, 0)), calc(-100% - var(--space-xs)));
      pointer-events: none;
    }

    .atlas-node-tooltip[data-side='bottom'] {
      transform: translate(calc(-50% + var(--atlas-tip-shift, 0)), var(--space-xs));
    }

    .atlas-node-tooltip .media {
      width: 3rem;
      height: 3rem;
      border-radius: 0.82rem;
      overflow: hidden;
      display: grid;
      place-items: center;
      background: rgba(11, 18, 31, 0.88);
      border: 1px solid rgba(176, 199, 236, 0.22);
      color: #eef5ff;
      font-size: 1.15rem;
    }

    .atlas-node-tooltip .media > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .atlas-node-tooltip .copy {
      min-width: 0;
      display: grid;
      gap: var(--space-xs);
    }

    .atlas-node-tooltip .copy > strong,
    .atlas-node-tooltip .copy > span {
      margin: 0;
      display: block;
    }

    .atlas-node-tooltip .copy > strong {
      color: #f5f9ff;
      font-size: 0.84rem;
      line-height: 1.15;
    }

    .atlas-node-tooltip .copy > span {
      color: var(--text-dim, #93a8c2);
      font-size: var(--space-sm);
      line-height: 1.32;
    }







  </style>
</atlas-map>
